const WHITE_SVG_FILL_VALUES = new Set([
  '#fff',
  '#ffffff',
  'white',
  'rgb(255,255,255)',
  'rgba(255,255,255,1)',
  'rgba(255,255,255,1.0)',
]);

export const WIRING_GUIDE_CANVAS_FILL = 'none';

const normalizeColorValue = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/\s+/g, '');

const getNumericSvgSize = (value) => {
  const parsed = Number.parseFloat(String(value || '').trim());
  return Number.isFinite(parsed) ? parsed : null;
};

const getSvgBounds = (svg) => {
  if (!(svg instanceof Element)) {
    return null;
  }

  const rawViewBox = String(svg.getAttribute('viewBox') || '').trim();
  if (rawViewBox) {
    const parts = rawViewBox
      .split(/[\s,]+/)
      .map((token) => Number.parseFloat(token))
      .filter((token) => Number.isFinite(token));

    if (parts.length === 4) {
      return {
        width: parts[2],
        height: parts[3],
      };
    }
  }

  const width = getNumericSvgSize(svg.getAttribute('width'));
  const height = getNumericSvgSize(svg.getAttribute('height'));

  if (!width || !height) {
    return null;
  }

  return { width, height };
};

const isFullCanvasDimension = (value, expected) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) {
    return true;
  }

  if (normalized === '100%') {
    return true;
  }

  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) && Number.isFinite(expected) && Math.abs(parsed - expected) < 0.5;
};

const isCanvasBackgroundRect = (rect, bounds) => {
  if (!(rect instanceof Element) || !bounds) {
    return false;
  }

  if (rect.parentElement?.tagName?.toLowerCase() !== 'svg') {
    return false;
  }

  const fill = normalizeColorValue(rect.getAttribute('fill') || rect.style?.getPropertyValue('fill'));
  if (!WHITE_SVG_FILL_VALUES.has(fill)) {
    return false;
  }

  const stroke = normalizeColorValue(rect.getAttribute('stroke') || rect.style?.getPropertyValue('stroke'));
  if (stroke && stroke !== 'none' && stroke !== 'transparent') {
    return false;
  }

  const x = getNumericSvgSize(rect.getAttribute('x')) ?? 0;
  const y = getNumericSvgSize(rect.getAttribute('y')) ?? 0;
  if (Math.abs(x) > 0.5 || Math.abs(y) > 0.5) {
    return false;
  }

  return (
    isFullCanvasDimension(rect.getAttribute('width'), bounds.width)
    && isFullCanvasDimension(rect.getAttribute('height'), bounds.height)
  );
};

export const normalizeWiringGuideSvgBackgroundElement = (svg, backgroundFill = WIRING_GUIDE_CANVAS_FILL) => {
  if (!(svg instanceof Element)) {
    return false;
  }

  const bounds = getSvgBounds(svg);
  if (!bounds) {
    return false;
  }

  let didUpdate = false;

  Array.from(svg.children)
    .filter((child) => child.tagName?.toLowerCase() === 'rect')
    .forEach((rect) => {
      if (!isCanvasBackgroundRect(rect, bounds)) {
        return;
      }

      rect.setAttribute('fill', backgroundFill);
      didUpdate = true;
    });

  return didUpdate;
};

export const normalizeWiringGuideSvgBackground = (svgContent, backgroundFill = WIRING_GUIDE_CANVAS_FILL) => {
  if (typeof svgContent !== 'string' || !svgContent.trim()) {
    return '';
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(svgContent, 'image/svg+xml');

  if (doc.querySelector('parsererror')) {
    return svgContent;
  }

  const svg = doc.querySelector('svg');
  if (!svg) {
    return svgContent;
  }

  if (!normalizeWiringGuideSvgBackgroundElement(svg, backgroundFill)) {
    return svgContent;
  }

  return new XMLSerializer().serializeToString(doc);
};
